import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Plus, Sparkles } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { API_BASE } from "@/lib/utils";
import { resolveMediaUrl } from "@/lib/media-api";

function normalizeEvent(row = {}) {
  return {
    id: String(row.id || ""),
    title: row.title || "Untitled event",
    venue: row.venue || "TBD",
    cover: row.cover || "",
    accent: row.accent || "var(--primary)",
    status: row.status || "Draft",
    registered: Number(row.registered || 0),
    checkedIn: Number(row.checkedIn || 0),
    capacity: Math.max(1, Number(row.capacity || 0)),
    revenue: Number(row.revenue || 0),
  };
}

const fmt = (n, prefix = "", suffix = "") => {
  if (typeof n !== "number" || !Number.isFinite(n)) return String(n ?? "—");
  return `${prefix}${n >= 1000 ? n.toLocaleString() : n}${suffix}`;
};

export default function Dashboard() {
  const [data, setData] = useState({ events: [], kpis: [], registrationsSeries: [], summary: null });
  const [period, setPeriod] = useState("30D");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    let firstLoad = true;
    const load = async () => {
      if (firstLoad) setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/api/admin/dashboard?period=${encodeURIComponent(period)}`);
        if (!res.ok) throw new Error(`Failed to load dashboard (${res.status})`);
        const payload = await res.json();
        if (!cancelled) {
          setData(payload);
          setError("");
        }
      } catch (err) {
        if (!cancelled) setError(err?.message || "Failed to load dashboard");
      } finally {
        if (!cancelled && firstLoad) {
          setLoading(false);
          firstLoad = false;
        }
      }
    };
    load();
    const timer = window.setInterval(load, 45000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [period]);

  const summary = data.summary || {};
  const events = Array.isArray(data.events) ? data.events.map(normalizeEvent).filter((e) => e.id) : [];
  const kpis = data.kpis || [];
  const chartData = useMemo(() => (Array.isArray(data.registrationsSeries) ? data.registrationsSeries : []), [data.registrationsSeries]);
  const chartTotal = useMemo(
    () => chartData.reduce((sum, row) => sum + Number(row?.v || 0), 0),
    [chartData],
  );

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1500px] mx-auto" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-overline">Operator overview</div>
          <h1 className="font-heading text-4xl tracking-tight font-medium mt-2">Workspace overview</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-2">
            {loading
              ? "Loading metrics..."
              : `${summary.activeEvents || 0} active · ${summary.setupEvents || 0} in setup · ${summary.closingSoon || 0} tracked`}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/admin/ai" className="btn-secondary" data-testid="dashboard-ai-search">
            <Sparkles size={14} /> AI Search
          </Link>
          <Link to="/admin/events" className="btn-secondary" data-testid="see-all-events">All events <ArrowRight size={14} /></Link>
          <Link to="/admin/events/new" className="btn-primary" data-testid="dashboard-new-event"><Plus size={14} /> New event</Link>
        </div>
      </div>

      {!loading && !error && events.length === 0 ? (
        <div className="mt-8 surface p-8 border-l-2 border-l-[var(--primary)]">
          <h2 className="font-heading text-xl font-medium">Create your first event</h2>
          <p className="text-sm text-[var(--text-secondary)] mt-2 max-w-xl">
            Start with basics, registration, and check-in. Your workspace is ready — add an event to open the full admin console.
          </p>
          <Link to="/admin/events/new" className="btn-primary inline-flex mt-6" data-testid="dashboard-onboarding-cta">
            <Plus size={14} /> Create event
          </Link>
        </div>
      ) : null}

      {/* KPI strip */}
      <div className="mt-10 grid grid-cols-1 md:grid-cols-4 gap-px bg-[var(--border-default)] border border-[var(--border-default)]">
        {(error ? [] : kpis).map((k) => (
          <div key={k.label} className="bg-[var(--bg-surface)] p-6 min-w-0">
            <div className="text-overline">{k.label}</div>
            <div className="font-mono text-3xl mt-3 text-[var(--text-primary)] truncate tabular-nums min-w-0">{fmt(k.value, k.prefix || "", k.suffix || "")}</div>
            {(k.caption || k.delta) ? (
              <div className="text-xs font-mono mt-2 text-[var(--text-tertiary)]">{k.caption || k.delta}</div>
            ) : null}
          </div>
        ))}
        {!loading && !error && kpis.length === 0 && <div className="bg-[var(--bg-surface)] p-6 text-sm text-[var(--text-secondary)]">No KPI data available.</div>}
      </div>

      {/* Chart + side */}
      <div className="mt-px grid grid-cols-1 md:grid-cols-12 gap-px bg-[var(--border-default)] border-x border-b border-[var(--border-default)]">
        <div className="md:col-span-8 bg-[var(--bg-surface)] p-4 md:p-8 min-h-[280px] md:min-h-[340px]">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="text-overline">Registrations · {period}</div>
              <div className="font-mono text-3xl mt-2">+{chartTotal.toLocaleString()}</div>
              <div className="text-xs font-mono text-[var(--text-tertiary)] mt-1">New registrations in selected period</div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Link to="/admin/reports/registrations" className="btn-ghost text-xs">
                All registrations →
              </Link>
              {["7D", "30D", "90D", "ALL"].map((p) => (
                <button
                  key={p}
                  onClick={() => setPeriod(p)}
                  className={`text-[11px] font-mono px-2.5 py-1 ${period === p ? "bg-[var(--primary)] text-[var(--on-primary)]" : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]"}`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
          {error ? (
            <div className="mt-6 text-sm text-[var(--error)]">{error}</div>
          ) : (
            <Chart data={chartData} />
          )}
        </div>

        <div className="md:col-span-4 bg-[var(--bg-surface)] p-4 md:p-8">
          <div className="text-overline mb-4">Today's check-ins</div>
          <div className="font-mono text-5xl">{(summary.todayCheckins || 0).toLocaleString()}</div>
          <div className="text-xs font-mono text-[var(--text-tertiary)] mt-2">
            {summary.totalCheckedIn != null
              ? `${Number(summary.totalCheckedIn).toLocaleString()} checked in overall · refreshes every 45s`
              : "From live check-in activity today"}
          </div>
          <div className="mt-8 space-y-3">
            {(summary.ops || []).map(({ label: k, value: v }) => (
              <div key={k} className="flex items-center justify-between text-sm py-2 border-b border-dashed border-[var(--border-default)]">
                <span className="text-[var(--text-secondary)]">{k}</span>
                <span className="font-mono">{v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Events grid */}
      <div className="mt-12 flex items-end justify-between mb-6">
        <div>
          <div className="text-overline">Active events</div>
          <h2 className="font-heading text-2xl mt-1">Your portfolio</h2>
        </div>
        <Link to="/admin/events" className="btn-ghost text-sm">View all →</Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {events.map((e) => (
          <Link
            to={`/admin/events/${e.id}`}
            key={e.id}
            data-testid={`event-card-${e.id}`}
            className="surface surface-hover group block fade-up"
          >
            <div className="aspect-[4/3] relative overflow-hidden bg-[var(--bg-elev)]">
              {resolveMediaUrl(e.cover) ? (
                <img
                  src={resolveMediaUrl(e.cover)}
                  alt=""
                  className="w-full h-full object-cover opacity-50 group-hover:opacity-70 group-hover:scale-105 transition-all duration-700"
                />
              ) : (
                <div
                  className="w-full h-full opacity-40"
                  style={{ background: `linear-gradient(135deg, ${e.accent}55 0%, var(--bg-elev) 60%)` }}
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-surface)] via-transparent to-transparent" />
              <span
                className="absolute top-3 left-3 text-[10px] font-mono uppercase tracking-widest px-2 py-1 border"
                style={{ color: e.accent, borderColor: e.accent + "55", background: "rgba(0,0,0,0.4)" }}
              >
                {e.status}
              </span>
              <div className="absolute bottom-3 right-3"><ArrowUpRight size={16} className="text-[var(--text-primary)]" /></div>
            </div>
            <div className="p-5">
              <div className="font-heading text-lg leading-tight truncate">{e.title}</div>
              <div className="text-xs text-[var(--text-tertiary)] font-mono mt-1 truncate">{e.venue}</div>
              <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
                <Stat label="Reg" v={e.registered.toLocaleString()} />
                <Stat label="In" v={e.checkedIn.toLocaleString()} />
                <Stat label="Cap" v={e.capacity.toLocaleString()} />
              </div>
              <div className="mt-4 h-1 bg-[var(--bg-elev)] overflow-hidden">
                <div
                  className="h-full max-w-full"
                  style={{
                    width: `${e.capacity > 0 ? Math.min(100, (e.registered / e.capacity) * 100) : 0}%`,
                    background: e.registered > e.capacity ? "var(--error)" : e.accent,
                  }}
                />
              </div>
            </div>
          </Link>
        ))}
        {!loading && !error && events.length === 0 && <div className="text-sm text-[var(--text-secondary)]">No events found.</div>}
      </div>
    </div>
  );
}

function Stat({ label, v }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-tertiary)]">{label}</div>
      <div className="font-mono text-[var(--text-primary)] mt-0.5">{v}</div>
    </div>
  );
}

function Chart({ data }) {
  if (!data?.length) {
    return <div className="mt-6 text-sm text-[var(--text-secondary)]">No registration trend data available.</div>;
  }

  const chartData = data.map((row) => ({
    label: row?.d || "",
    registrations: Number(row?.v || 0),
  }));
  const xTickInterval = chartData.length > 14 ? Math.max(0, Math.ceil(chartData.length / 7) - 1) : 0;

  return (
    <div className="h-[240px] w-full mt-6" data-testid="registrations-chart">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="registrationsGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--primary)" stopOpacity={0.35} />
              <stop offset="100%" stopColor="var(--primary)" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="var(--border-default)" strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="label"
            interval={xTickInterval}
            minTickGap={24}
            tick={{ fill: "var(--text-tertiary)", fontSize: 10, fontFamily: "ui-monospace, monospace" }}
            axisLine={{ stroke: "var(--border-default)" }}
            tickLine={false}
          />
          <YAxis
            tick={{ fill: "var(--text-tertiary)", fontSize: 10, fontFamily: "ui-monospace, monospace" }}
            axisLine={false}
            tickLine={false}
            width={36}
            allowDecimals={false}
          />
          <Tooltip
            cursor={{ stroke: "var(--border-default)", strokeDasharray: "4 4" }}
            contentStyle={{
              background: "var(--bg-elev)",
              border: "1px solid var(--border-default)",
              borderRadius: 0,
              fontSize: 11,
              fontFamily: "ui-monospace, monospace",
              color: "var(--text-primary)",
            }}
            labelFormatter={(label) => String(label || "—")}
            formatter={(value) => [Number(value).toLocaleString(), "Registrations"]}
          />
          <Legend
            verticalAlign="top"
            align="left"
            iconType="plainline"
            wrapperStyle={{
              fontSize: 11,
              fontFamily: "ui-monospace, monospace",
              color: "var(--text-secondary)",
              paddingBottom: 4,
            }}
          />
          <Area
            type="monotone"
            dataKey="registrations"
            name="New registrations"
            stroke="var(--primary)"
            strokeWidth={2}
            fill="url(#registrationsGradient)"
            activeDot={{ r: 4, fill: "var(--primary)", stroke: "var(--bg-surface)", strokeWidth: 2 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
